import React, { useState, useEffect } from 'react';
import { authHelpers } from '../global/supabase';
import '../global/styles.css';

function CompleteProfile({ onComplete }) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    stateLicenseNumber: '',
    pin: ''
  });
  const [userEmail, setUserEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPin, setShowPin] = useState(false);

  useEffect(() => {
    const loadUserEmail = async () => {
      const { data: { user } } = await authHelpers.supabase.auth.getUser();
      if (user?.email) {
        setUserEmail(user.email);
      }
    };
    loadUserEmail();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (!/^\d{6}$/.test(formData.pin)) {
      setError('PIN must be exactly 6 digits');
      setLoading(false);
      return;
    }

    try {
      // Get current user
      const { data: { user } } = await authHelpers.supabase.auth.getUser();

      if (!user) {
        setError('No authenticated user found');
        setLoading(false);
        return;
      }

      // Names and license through the RPC, then the PIN through set_own_pin
      // (hashed server-side; it also marks the profile completed).
      let { error: profileError } = await authHelpers.supabase.rpc('update_user_metadata', {
        user_id: user.id,
        first_name: formData.firstName,
        last_name: formData.lastName,
        state_license_number: formData.stateLicenseNumber,
      });
      if (!profileError) {
        ({ error: profileError } = await authHelpers.supabase.rpc('set_own_pin', { new_pin: formData.pin }));
      }

      if (profileError) {
        setError(profileError.message);
      } else {
        // Success - profile completed
        onComplete();
      }
    } catch (err) {
      setError('An unexpected error occurred');
    }

    setLoading(false);
  };

  return (
    <div className="container-center">
      <div className="card">
        <h2 className="title-auth">Complete Your Profile</h2>
        <p className="subtitle-auth">Please provide the following information to finish setting up your account</p>

        {error && (
          <div className="message error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email" className="form-label">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={userEmail}
              disabled
              className="form-input"
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="firstName" className="form-label">First Name</label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="lastName" className="form-label">Last Name</label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="stateLicenseNumber" className="form-label">State License Number</label>
            <input
              type="text"
              id="stateLicenseNumber"
              name="stateLicenseNumber"
              value={formData.stateLicenseNumber}
              onChange={handleChange}
              required
              className="form-input"
              placeholder="Enter your state license number"
            />
          </div>

          <div className="form-group">
            <label htmlFor="pin" className="form-label">PIN (6 digits)</label>
            <div className="input-container">
              <input
                type={showPin ? "text" : "password"}
                id="pin"
                name="pin"
                value={formData.pin}
                onChange={handleChange}
                required
                minLength="6"
                maxLength="6"
                inputMode="numeric"
                pattern="[0-9]*"
                className="form-input input-with-icon"
                placeholder="Enter a 6-digit PIN"
              />
              <button
                type="button"
                className="input-icon"
                onClick={() => setShowPin(!showPin)}
                aria-label={showPin ? "Hide PIN" : "Show PIN"}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  {showPin ? (
                    <>
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </>
                  ) : (
                    <>
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </>
                  )}
                </svg>
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-full"
            disabled={loading}
          >
            {loading ? 'Completing Profile...' : 'Complete Profile'}
          </button>
        </form>
      </div>

      <footer className="footer">
        <p className="text-small">NarcoticTrack © 2025 NarcoticTrack Inventory System, LLC</p>
      </footer>
    </div>
  );
}

export default CompleteProfile;
